import type { 
  GetCrmMiscMetricsData,
  GetCrmMiscMetricsResponse,

  GetCrmMiscTemplateVariablesData,
  GetCrmMiscTemplateVariablesResponse,

  GetCrmSegmentsData,
  GetCrmSegmentsResponse,

  PostCrmSegmentsStatsData,
  PostCrmSegmentsStatsResponse,

  PostCrmSegmentsByIdStatsData,
  PostCrmSegmentsByIdStatsResponse,

  PostCrmSegmentsData,
  PostCrmSegmentsResponse,

  GetCrmSegmentsByIdData,
  GetCrmSegmentsByIdResponse,

  PutCrmSegmentsByIdData,
  PutCrmSegmentsByIdResponse,

  DeleteCrmSegmentsByIdData,
  DeleteCrmSegmentsByIdResponse,

  GetCrmSegmentsByIdCommentsData,
  GetCrmSegmentsByIdCommentsResponse,

  PostCrmSegmentsByIdCommentsData,
  PostCrmSegmentsByIdCommentsResponse,

  GetCrmSegmentsByIdPlayersData,
  GetCrmSegmentsByIdPlayersResponse,

  GetCrmActionGroupsByIdPlayersData,
  GetCrmActionGroupsByIdPlayersResponse,

  GetCrmActivitiesByIdPlayersData,
  GetCrmActivitiesByIdPlayersResponse,

  GetCrmActivitiesData,
  GetCrmActivitiesResponse,

  PostCrmActivitiesData,
  PostCrmActivitiesResponse,

  GetCrmActivitiesByIdStatsData,
  GetCrmActivitiesByIdStatsResponse,

  GetCrmActivitiesByIdData,
  GetCrmActivitiesByIdResponse,

  PutCrmActivitiesByIdData,
  PutCrmActivitiesByIdResponse,

  PostCrmActivitiesSendTestEmailData,
  PostCrmActivitiesSendTestEmailResponse,

  PostCrmActivitiesSendTestSmsData,
  PostCrmActivitiesSendTestSmsResponse,

  GetCrmActivitiesByIdCommentsData,
  GetCrmActivitiesByIdCommentsResponse,

  PostCrmActivitiesByIdCommentsData,
  PostCrmActivitiesByIdCommentsResponse,

  PostCrmActivitiesEmailTemplatePreviewData,
  PostCrmActivitiesEmailTemplatePreviewResponse,

  PutCrmActivitiesByIdMetricsData,
  PutCrmActivitiesByIdMetricsResponse,

  GetCrmMiscCallCenterProjectsData,
  GetCrmMiscCallCenterProjectsResponse,

  GetCrmMiscEmailTemplatesData,
  GetCrmMiscEmailTemplatesResponse
} from "@/types/openapi-ts";

import type { CrmActivityStatus } from "@/types/status";
import type { AxiosInstance } from "axios";

export function getCrmApiModule(affiliatesApi: AxiosInstance) {
  return {
    getCrmMetrics() {
      return affiliatesApi.get<GetCrmMiscMetricsResponse>('/crm/misc/metrics');
    },
    getCrmTemplateVariables() {
      return affiliatesApi.get<GetCrmMiscTemplateVariablesResponse>('/crm/misc/template-variables');
    },
    getSegments(params: GetCrmSegmentsData['query']) {
      return affiliatesApi.get<GetCrmSegmentsResponse>('/crm/segments', { params });
    },
    getSegmentStats(params: PostCrmSegmentsStatsData['body']) {
      return affiliatesApi.post<PostCrmSegmentsStatsResponse>('/crm/segments/stats', params);
    },
    getSegmentStatsById(id: string, filters: PostCrmSegmentsByIdStatsData['body']['filters'] = []) {
      return affiliatesApi.post<PostCrmSegmentsByIdStatsResponse>(`/crm/segments/${id}/stats`, { filters });
    },

    postSegments(payload: PostCrmSegmentsData['body']) {
      return affiliatesApi.post<PostCrmSegmentsResponse>('/crm/segments', payload);
    },

    getSegment(id: string) {
      return affiliatesApi.get<GetCrmSegmentsByIdResponse>(`/crm/segments/${id}`);
    },
    putSegment(id: string, payload: PutCrmSegmentsByIdData['body']) {
      return affiliatesApi.put<PutCrmSegmentsByIdResponse>(`/crm/segments/${id}`, payload);
    },
    deleteSegment(id: string) {
      return affiliatesApi.delete<DeleteCrmSegmentsByIdResponse>(`/crm/segments/${id}`);
    },
    postSegmentChangeStatus(id: string, status: string) {
      return affiliatesApi.post<void>(`/crm/segments/${id}/${status}`);
    },

    getSegmentComments(id: string) {
      return affiliatesApi.get<GetCrmSegmentsByIdCommentsResponse>(`/crm/segments/${id}/comments?sort_dir=asc`);
    },
    postSegmentComments(id: string, data: PostCrmSegmentsByIdCommentsData['body']) {
      return affiliatesApi.post<PostCrmSegmentsByIdCommentsResponse>(`/crm/segments/${id}/comments`, data);
    },
    getSegmentPlayers(id: string) {
      return affiliatesApi.get<GetCrmSegmentsByIdPlayersResponse>(`/crm/segments/${id}/players`);
    },
    getActionGroupsPlayers(id: string) {
      return affiliatesApi.get<GetCrmActionGroupsByIdPlayersResponse>(`/crm/action-groups/${id}/players`);
    },
    getActivityPlayers(id: string) {
      return affiliatesApi.get<GetCrmActivitiesByIdPlayersResponse>(`/crm/activities/${id}/players`);
    },

    getActivities(params: GetCrmActivitiesData['query']) {
      return affiliatesApi.get<GetCrmActivitiesResponse>('/crm/activities', { params });
    },
    postActivity(payload: PostCrmActivitiesData['body']) {
      return affiliatesApi.post<PostCrmActivitiesResponse>('/crm/activities', payload);
    },
    getActivityStatsById(id: string) {
      return affiliatesApi.get<GetCrmActivitiesByIdStatsResponse>(`/crm/activities/${id}/stats`);
    },
    getActivity(id: string, params: GetCrmActivitiesByIdData['query']) {
      if (params) {
        return affiliatesApi.get<GetCrmActivitiesByIdResponse>(`/crm/activities/${id}`, { params });
      }

      return affiliatesApi.get<GetCrmActivitiesByIdResponse>(`/crm/activities/${id}`);
    },
    putActivity(id: string, payload: PutCrmActivitiesByIdData['body']) {
      return affiliatesApi.put<PutCrmActivitiesByIdResponse>(`/crm/activities/${id}`, payload);
    },
    sendActivityTestEmails(payload: PostCrmActivitiesSendTestEmailData['body']) {
      return affiliatesApi.post<PostCrmActivitiesSendTestEmailResponse>('/crm/activities/send-test-email', payload);
    },
    sendActivityTestSms(payload: PostCrmActivitiesSendTestSmsData['body']) {
      return affiliatesApi.post<PostCrmActivitiesSendTestSmsResponse>('/crm/activities/send-test-sms', payload);
    },
    // deleteActivity(id) {
    //   return affiliatesApi.delete(`/crm/activities/${id}`);
    // },
    // putActivityStatus(id, status) {
    //   return affiliatesApi.put(`/crm/activities/${id}/status`, { status });
    // },
    postActivityStatus(id: string, status: CrmActivityStatus, payload?: any) {
      return affiliatesApi.post<void>(`/crm/activities/${id}/${status}`, payload);
    },
    getActivityComments(id: string, params: GetCrmActivitiesByIdCommentsData['query']) {
      return affiliatesApi.get<GetCrmActivitiesByIdCommentsResponse>(`/crm/activities/${id}/comments`, { params });
    },
    postActivityComment(id: string, comment: PostCrmActivitiesByIdCommentsData['body']) {
      return affiliatesApi.post<PostCrmActivitiesByIdCommentsResponse>(`/crm/activities/${id}/comments`, comment);
    },
    postEmailTemplatePreview(payload: PostCrmActivitiesEmailTemplatePreviewData['body']) {
      const { site_id, markdown, crm_mail_template_id } = payload;
      return affiliatesApi.post<PostCrmActivitiesEmailTemplatePreviewResponse>(
        '/crm/activities/email-template-preview', 
        {
          site_id,
          markdown,
          crm_mail_template_id,
        }
      );
    },
    updateActivityMetrics(id: string, metrics: PutCrmActivitiesByIdMetricsData['body']['metrics']) {
      return affiliatesApi.put<PutCrmActivitiesByIdMetricsResponse>(`crm/activities/${id}/metrics`, { metrics });
    },
    getCallCenterProjects(params: GetCrmMiscCallCenterProjectsData['query']) {
      return affiliatesApi.get<GetCrmMiscCallCenterProjectsResponse>('/crm/misc/call-center/projects', { params });
    },
    getEmailTemplates(params: GetCrmMiscEmailTemplatesData['query']) {
      return affiliatesApi.get<GetCrmMiscEmailTemplatesResponse>('/crm/misc/email-templates', { params });
    },
  }
}
